const HOURS_IN_DAY = 24;
const HOURS_IN_HALF_DAY = 12;
const MINUTES_IN_HOUR = 60;
const pad = (value: number): string => String(value).padStart(2, '0');

// "10:06", "10.06", "10.6", "10 6" (hours, a separator, minutes) or "1006" / "930" (digits only).
const SEPARATED = /^(\d{1,2})[:.\s](\d{1,2})$/;
const DIGITS_ONLY = /^(\d{1,2})(\d{2})$/;
// D10: an am/pm on the end — "3:45 pm", "3:45p", "3.45PM", "3 p.m." — and with it a bare hour, "3pm".
const MERIDIEM = /^(.*?)\s*([ap])\.?(?:m\.?)?$/i;
const HOUR_ONLY = /^(\d{1,2})$/;

function clockOf(text: string): { readonly hours: number; readonly minutes: number } | null {
  const match = SEPARATED.exec(text) ?? DIGITS_ONLY.exec(text);
  if (!match?.[1] || !match[2]) return null;
  return { hours: Number(match[1]), minutes: Number(match[2]) };
}

/** "3:45" with "pm" is 15:45; 12 am is midnight and 12 pm is noon. Hours past 12 refuse an am/pm. */
function withMeridiem(rest: string, meridiem: string): string | null {
  const hourOnly = HOUR_ONLY.exec(rest);
  const clock = hourOnly?.[1] ? { hours: Number(hourOnly[1]), minutes: 0 } : clockOf(rest);
  if (!clock || clock.hours < 1 || clock.hours > HOURS_IN_HALF_DAY || clock.minutes >= MINUTES_IN_HOUR) return null;
  const isPm = meridiem.toLowerCase() === 'p';
  const hours = (clock.hours % HOURS_IN_HALF_DAY) + (isPm ? HOURS_IN_HALF_DAY : 0);
  return `${pad(hours)}:${pad(clock.minutes)}`;
}

/**
 * A time typed into the time picker's filter box as 24-hour `HH:mm`, or null when it isn't one.
 * The owner writes 10:06 as "10.6", so a single minute digit is the minute itself, not tens.
 * Without an am/pm the hours are 24-hour ("15:45"); with one they are 1–12 ("3:45 pm").
 */
export function typedTime(text: string): string | null {
  const trimmed = text.trim();
  const meridiem = MERIDIEM.exec(trimmed);
  if (meridiem?.[1] !== undefined && meridiem[2]) return withMeridiem(meridiem[1], meridiem[2]);
  const clock = clockOf(trimmed);
  if (!clock || clock.hours >= HOURS_IN_DAY || clock.minutes >= MINUTES_IN_HOUR) return null;
  return `${pad(clock.hours)}:${pad(clock.minutes)}`;
}
